import { ProjectIdSchema } from "@storytell/types";
import z from "zod";
import { RequestBuilder } from "../requestBuilder";
import {
  AutocompleteBodySchema,
  AutocompleteResponseSchema,
  ChatIdMessageIdParamsSchema,
  ChatIdParamsSchema,
  ClassifyModeBodySchema,
  ClassifyModeResponseSchema,
  ForkChatBodySchema,
  ForkChatResponseSchema,
  ImprovePromptBodySchema,
  ImprovePromptResponseSchema,
} from "./chatClient.types";

export const DeleteChatRequestConfig = new RequestBuilder({
  paramsSchema: ChatIdParamsSchema,
  path: (params) => `/chats/${params.id}`,
})
  .withMethod("DELETE")
  .withContentType(undefined)
  .withIncludeProjectId();

export const DeleteMessageRequestConfig = new RequestBuilder({
  paramsSchema: ChatIdMessageIdParamsSchema,
  path: (params) => `/chats/${params.id}/messages/${params.messageId}`,
})
  .withMethod("DELETE")
  .withContentType(undefined)
  .withIncludeProjectId();

const MoveChatsRequestSchema = z.object({
  chats: z.array(
    z.object({
      chatId: z.string(),
      projectId: ProjectIdSchema,
    })
  ),
});

export type MoveChatsRequest = z.infer<typeof MoveChatsRequestSchema>;

export const MoveChatsRequestConfig = new RequestBuilder({
  path: () => "/chats-move",
  bodySchema: MoveChatsRequestSchema,
})
  .withMethod("POST")
  .withContentType(undefined)
  .withIncludeProjectId();

export const ForkChatRequestConfig = new RequestBuilder({
  paramsSchema: ChatIdParamsSchema,
  bodySchema: ForkChatBodySchema,
  responseSchema: ForkChatResponseSchema,
  path: (params) => `/chats/${params.id}/fork`,
})
  .withMethod("POST")
  .withContentType("application/json")
  .withIncludeProjectId();

// Items stay unknown so one malformed suggestion doesn't fail the whole response: the app's
// normalizer keeps what it can read and drops the rest. Empty until the message's suggestions
// are generated, and for messages that never get any.
const FollowUpSuggestionsResponseSchema = z.object({
  suggestions: z
    .array(z.unknown())
    .nullable()
    .transform((v) => v ?? []),
});

export const FollowUpSuggestionsRequestConfig = new RequestBuilder({
  paramsSchema: ChatIdMessageIdParamsSchema,
  responseSchema: FollowUpSuggestionsResponseSchema,
  path: (params) => `/chats/${params.id}/messages/${params.messageId}/followups`,
})
  .withMethod("GET")
  .withContentType(undefined)
  .withIncludeProjectId();

const SteerChatBodySchema = z.object({
  text: z.string().min(1),
});

// The running assistant message the steer was delivered into, and the id the
// resulting user_interjection block carries so the composer can match it.
// steerId is optional only so a 202 from a pod still on the previous build
// during a rolling deploy reads as sent rather than failed.
const SteerChatResponseSchema = z.object({
  messageId: z.string(),
  steerId: z.string().optional(),
});

// 202 while a turn is running; 409 with code chats.steer.no_running_turn otherwise.
export const SteerChatRequestConfig = new RequestBuilder({
  paramsSchema: ChatIdParamsSchema,
  bodySchema: SteerChatBodySchema,
  responseSchema: SteerChatResponseSchema,
  path: (params) => `/chats/${params.id}/steer`,
})
  .withMethod("POST")
  .withContentType("application/json")
  .withIncludeProjectId();

const UpdateChatTitleBodySchema = z.object({
  title: z.string().min(1).max(255),
});

export const ChatTitleResponseSchema = z.object({
  chatId: z.string(),
  title: z.string(),
  modifiedAt: z.string(),
});

export type ChatTitleResponse = z.infer<typeof ChatTitleResponseSchema>;

export const UpdateChatTitleRequestConfig = new RequestBuilder({
  paramsSchema: ChatIdParamsSchema,
  bodySchema: UpdateChatTitleBodySchema,
  responseSchema: ChatTitleResponseSchema,
  path: (params) => `/chats/${params.id}/title`,
})
  .withMethod("POST")
  .withContentType("application/json")
  .withIncludeProjectId();

export const RegenerateChatTitleRequestConfig = new RequestBuilder({
  paramsSchema: ChatIdParamsSchema,
  responseSchema: ChatTitleResponseSchema,
  path: (params) => `/chats/${params.id}/regenerate-title`,
})
  .withMethod("POST")
  .withContentType("application/json")
  .withIncludeProjectId();

export const ShareStatusResponseSchema = z.object({
  isShared: z.boolean(),
  token: z.string().optional(),
  createdAt: z.string().optional(),
});

export type ShareStatusResponse = z.infer<typeof ShareStatusResponseSchema>;

export const GetShareStatusRequestConfig = new RequestBuilder({
  paramsSchema: ChatIdParamsSchema,
  responseSchema: ShareStatusResponseSchema,
  path: (params) => `/chats/${params.id}/share`,
})
  .withMethod("GET")
  .withContentType(undefined)
  .withIncludeProjectId();

export const CreateShareLinkRequestConfig = new RequestBuilder({
  paramsSchema: ChatIdParamsSchema,
  responseSchema: ShareStatusResponseSchema,
  path: (params) => `/chats/${params.id}/share`,
})
  .withMethod("POST")
  .withContentType("application/json")
  .withIncludeProjectId();

export const RevokeShareLinkRequestConfig = new RequestBuilder({
  paramsSchema: ChatIdParamsSchema,
  path: (params) => `/chats/${params.id}/share`,
})
  .withMethod("DELETE")
  .withContentType(undefined)
  .withIncludeProjectId();

const SendShareEmailsBodySchema = z.object({
  emails: z.array(z.email()).min(1).max(10),
  textFragment: z.string().optional(),
});

const FailedEmailSchema = z.object({
  email: z.string(),
  reason: z.string(),
});

export const SendShareEmailsResponseSchema = z.object({
  sent: z.number(),
  failed: z.array(FailedEmailSchema).optional(),
});

export type SendShareEmailsResponse = z.infer<typeof SendShareEmailsResponseSchema>;

export const SendShareEmailsRequestConfig = new RequestBuilder({
  paramsSchema: ChatIdParamsSchema,
  bodySchema: SendShareEmailsBodySchema,
  responseSchema: SendShareEmailsResponseSchema,
  path: (params) => `/chats/${params.id}/share/email`,
})
  .withMethod("POST")
  .withContentType("application/json")
  .withIncludeProjectId();

// Resource-visibility level: project (everyone in the project), restricted
// (owner + the audience), private (owner only). Distinct from public-token sharing.
export const ChatVisibilityLevelSchema = z.enum(["project", "restricted", "private"]);

export type ChatVisibilityLevel = z.infer<typeof ChatVisibilityLevelSchema>;

const SetChatVisibilityBodySchema = z.object({
  visibility: ChatVisibilityLevelSchema,
  memberIds: z.array(z.string()).optional(),
});

export const SetChatVisibilityResponseSchema = z.object({
  chatId: z.string(),
  visibility: ChatVisibilityLevelSchema,
  memberIds: z.array(z.string()),
});

export type SetChatVisibilityResponse = z.infer<typeof SetChatVisibilityResponseSchema>;

export const ChatMembersResponseSchema = z.object({
  chatId: z.string(),
  memberIds: z.array(z.string()),
});

export type ChatMembersResponse = z.infer<typeof ChatMembersResponseSchema>;

export const SetChatVisibilityRequestConfig = new RequestBuilder({
  paramsSchema: ChatIdParamsSchema,
  bodySchema: SetChatVisibilityBodySchema,
  responseSchema: SetChatVisibilityResponseSchema,
  path: (params) => `/chats/${params.id}/visibility`,
})
  .withMethod("POST")
  .withContentType("application/json")
  .withIncludeProjectId();

export const GetChatMembersRequestConfig = new RequestBuilder({
  paramsSchema: ChatIdParamsSchema,
  responseSchema: ChatMembersResponseSchema,
  path: (params) => `/chats/${params.id}/members`,
})
  .withMethod("GET")
  .withContentType(undefined)
  .withIncludeProjectId();

export const ImprovePromptRequestConfig = new RequestBuilder({
  bodySchema: ImprovePromptBodySchema,
  responseSchema: ImprovePromptResponseSchema,
  path: () => "/chats/improve-prompt",
})
  .withMethod("POST")
  .withContentType("application/json")
  .withIncludeProjectId();

export const AutocompleteRequestConfig = new RequestBuilder({
  bodySchema: AutocompleteBodySchema,
  responseSchema: AutocompleteResponseSchema,
  path: () => "/chats/autocomplete",
})
  .withMethod("POST")
  .withContentType("application/json")
  .withIncludeProjectId();

export const ClassifyModeRequestConfig = new RequestBuilder({
  bodySchema: ClassifyModeBodySchema,
  responseSchema: ClassifyModeResponseSchema,
  path: () => "/chats/classify",
})
  .withMethod("POST")
  .withContentType("application/json")
  .withIncludeProjectId();

export const SearchChatsParamsSchema = z.object({
  q: z.string().min(1),
  limit: z.number().int().positive().max(100).optional(),
  from: z.enum(["user", "assistant"]).optional(),
  after: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  before: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  scope: z.enum(["messages", "title"]).optional(),
});

export type SearchChatsParams = z.infer<typeof SearchChatsParamsSchema>;

export const SearchChatsHitSchema = z.object({
  // "message" hits anchor to a specific turn; "title" hits surfaced via the chat title alone.
  kind: z.enum(["message", "title"]),
  chatId: z.string(),
  // Present only when kind === "message".
  messageId: z.string().optional(),
  role: z.enum(["user", "assistant"]).optional(),
  chatTitle: z.string(),
  // Server-rendered snippet with literal <mark>…</mark> spans from ts_headline.
  snippet: z.string(),
  createdAt: z.string(),
  score: z.number(),
});

export type SearchChatsHit = z.infer<typeof SearchChatsHitSchema>;

export const SearchChatsResponseSchema = z.object({
  hits: z.array(SearchChatsHitSchema),
});

export type SearchChatsResponse = z.infer<typeof SearchChatsResponseSchema>;

export const SearchChatsRequestConfig = new RequestBuilder({
  paramsSchema: SearchChatsParamsSchema,
  responseSchema: SearchChatsResponseSchema,
  path: () => "/chats/search",
})
  .withMethod("GET")
  .withContentType(undefined)
  .withQueryParams((params) => {
    if (!params) return undefined;
    const out: Record<string, string> = { q: params.q };
    if (params.limit !== undefined) out.limit = String(params.limit);
    if (params.from !== undefined) out.from = params.from;
    if (params.after !== undefined) out.after = params.after;
    if (params.before !== undefined) out.before = params.before;
    if (params.scope !== undefined) out.scope = params.scope;
    return out;
  })
  .withIncludeProjectId();
